import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Chip } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { FileText, CheckCircle, XCircle } from "@phosphor-icons/react";
import { VEHICLE_TYPE_MAP, FUEL_TYPE_MAP, VEHICLE_STATUS_LABELS, VEHICLE_STATUS_VARIANTS, PASS_STATUS_VARIANTS, APPROVAL_STATUS_LABELS, APPROVAL_STATUS_VARIANTS } from "@/lib/parkingTypes";

function Field({ label, value }) {
    return (
        <div>
            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-0.5">{label}</p>
            <p className="text-sm font-medium text-brand-ink">{value || "—"}</p>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="space-y-3">
            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft font-medium border-b border-brand-line pb-2">{title}</p>
            <div className="grid grid-cols-2 gap-3">{children}</div>
        </div>
    );
}

// Read-only Vehicle Management V2 profile — consolidates Vehicle / Owner (via flat_residents) /
// Parking (via the existing parking_allocations/parking_passes, read-only reference only), plus
// the Vehicle Approval V2 status + Approve/Reject actions when pending.
export default function VehicleProfileDialog({ open, onOpenChange, vehicle, onApprove, onReject, approving }) {
    if (!vehicle) return null;
    const status = vehicle.status || (vehicle.is_active === false ? "inactive" : "active");
    const approvalStatus = vehicle.approval_status || "approved";
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-h-[85vh] overflow-y-auto max-w-lg" data-testid="vehicle-profile-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-3 flex-wrap">
                        <span className="font-mono">{vehicle.registration_number}</span>
                        <Chip variant={VEHICLE_STATUS_VARIANTS[status] || "default"}>{VEHICLE_STATUS_LABELS[status] || status}</Chip>
                        <Chip variant={APPROVAL_STATUS_VARIANTS[approvalStatus] || "default"}>{APPROVAL_STATUS_LABELS[approvalStatus] || approvalStatus}</Chip>
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-6">
                    {approvalStatus === "pending" && (
                        <div className="bg-[#FFF7ED] border border-[#FFEDD5] rounded-md p-3 flex items-center justify-between gap-3" data-testid="vehicle-profile-approval-banner">
                            <p className="text-sm text-brand-ink">This vehicle is awaiting approval and cannot be assigned a parking slot yet.</p>
                            <div className="flex items-center gap-2 shrink-0">
                                <Button size="sm" disabled={approving} data-testid="vehicle-profile-approve-btn"
                                    onClick={() => onApprove?.(vehicle)} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white text-xs h-7">
                                    <CheckCircle size={13} className="mr-1" /> Approve
                                </Button>
                                <Button size="sm" variant="outline" disabled={approving} data-testid="vehicle-profile-reject-btn"
                                    onClick={() => onReject?.(vehicle)} className="rounded-full border-brand-line text-xs h-7">
                                    <XCircle size={13} className="mr-1" /> Reject
                                </Button>
                            </div>
                        </div>
                    )}
                    {approvalStatus === "rejected" && vehicle.rejection_reason && (
                        <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-md p-3" data-testid="vehicle-profile-rejection-reason">
                            <p className="text-[10px] uppercase tracking-overline text-[#DC2626] mb-1">Rejection reason</p>
                            <p className="text-sm text-brand-ink">{vehicle.rejection_reason}</p>
                        </div>
                    )}

                    <Section title="Vehicle">
                        <Field label="Registration number" value={vehicle.registration_number} />
                        <Field label="Type" value={VEHICLE_TYPE_MAP[vehicle.vehicle_type]?.label || vehicle.vehicle_type} />
                        <Field label="Brand" value={vehicle.make} />
                        <Field label="Model" value={vehicle.model} />
                        <Field label="Colour" value={vehicle.color} />
                        <Field label="Fuel" value={FUEL_TYPE_MAP[vehicle.fuel_type]?.label || vehicle.fuel_type} />
                        <Field label="Registration state" value={vehicle.registration_state} />
                    </Section>

                    <Section title="Owner">
                        <Field label="Resident" value={vehicle.resident_name} />
                        <Field label="Flat" value={vehicle.flat_label} />
                        <Field label="Relationship" value={vehicle.relationship_label} />
                    </Section>

                    <Section title="Parking">
                        <Field label="Parking slot" value={vehicle.parking_slot_number} />
                        <div>
                            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-0.5">Parking pass</p>
                            {vehicle.parking_pass_number ? (
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-brand-ink font-mono">{vehicle.parking_pass_number}</span>
                                    <Chip variant={PASS_STATUS_VARIANTS[vehicle.parking_pass_status] || "default"}>{vehicle.parking_pass_status}</Chip>
                                </div>
                            ) : <p className="text-sm font-medium text-brand-ink">—</p>}
                        </div>
                    </Section>

                    <div className="space-y-3">
                        <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft font-medium border-b border-brand-line pb-2">Documents</p>
                        <div data-testid="vehicle-documents-coming-soon" className="flex items-center gap-2 text-sm text-brand-inkSoft bg-brand-bg border border-dashed border-brand-line rounded-sm px-3 py-4 justify-center">
                            <FileText size={16} />
                            <span>Coming soon — RC / Insurance / PUC documents are not part of this phase yet.</span>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
